import { useState } from 'react';

import { Button, FieldLabel, NumericInput, showSnackbar, TextArea, TextInput } from 'fluxo-ui';
import { CheckIcon, EditIcon, PlusIcon, TrashIcon } from 'fluxo-ui/icons';

import type { ComponentEvent } from 'fluxo-ui';

interface Option {
    id: string;
    text: string;
    isCorrect: boolean;
    partialCredit?: number;
    feedback?: string;
    description?: string; // Why this option is right/wrong
}

interface OptionEditorProps {
    options?: Option[];
    onChange?: (options: Option[]) => void;
    questionType?: 'mcq_single' | 'mcq_multiple' | 'true_false';
    disabled?: boolean;
    allowPartialCredit?: boolean;
}

const OptionEditor: React.FC<OptionEditorProps> = ({
    options = [],
    onChange,
    questionType = 'mcq_single',
    disabled = false,
    allowPartialCredit = false,
}) => {
    const [optionList, setOptionList] = useState<Option[]>(
        options.length > 0
            ? options
            : questionType === 'true_false'
              ? [
                    { id: '1', text: 'True', isCorrect: false },
                    { id: '2', text: 'False', isCorrect: false },
                ]
              : [
                    { id: '1', text: '', isCorrect: false },
                    { id: '2', text: '', isCorrect: false },
                ],
    );

    const [expandedOption, setExpandedOption] = useState<string | null>(null);

    const handleUpdate = (updatedOptions: Option[]) => {
        setOptionList(updatedOptions);
        onChange?.(updatedOptions);
    };

    const addOption = () => {
        if (questionType === 'true_false') {
            showSnackbar('True/False questions have fixed options', 'Cannot Add', { type: 'warning' });
            return;
        }

        const newOption: Option = {
            id: Date.now().toString(),
            text: '',
            isCorrect: false,
        };
        handleUpdate([...optionList, newOption]);
    };

    const removeOption = (optionId: string) => {
        if (optionList.length <= 2) {
            showSnackbar('Must have at least 2 options', 'Cannot Remove', { type: 'warning' });
            return;
        }
        handleUpdate(optionList.filter((opt) => opt.id !== optionId));
    };

    const updateOption = (optionId: string, updates: Partial<Option>) => {
        handleUpdate(optionList.map((opt) => (opt.id === optionId ? { ...opt, ...updates } : opt)));
    };

    const toggleCorrect = (optionId: string) => {
        if (questionType === 'mcq_single' || questionType === 'true_false') {
            // For single answer, only one can be correct
            handleUpdate(
                optionList.map((opt) => ({
                    ...opt,
                    isCorrect: opt.id === optionId,
                })),
            );
        } else {
            // For multiple answer, toggle individually
            handleUpdate(optionList.map((opt) => (opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt)));
        }
    };

    const correctCount = optionList.filter((opt) => opt.isCorrect).length;
    const hasAllOptionsText = optionList.every((opt) => opt.text.trim().length > 0);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <label className="block text-sm font-medium text-slate-900 dark:text-white mb-1">Answer Options</label>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        {questionType === 'mcq_single'
                            ? 'Select one correct answer'
                            : questionType === 'mcq_multiple'
                              ? 'Select all correct answers'
                              : 'Select the correct answer'}
                    </p>
                </div>
                {questionType !== 'true_false' && (
                    <Button
                        label="Add Option"
                        leftIcon={PlusIcon}
                        variant="default"
                        size="sm"
                        layout="outlined"
                        onClick={addOption}
                        disabled={disabled || optionList.length >= 10}
                    />
                )}
            </div>

            {/* Validation Alert */}
            {correctCount === 0 && hasAllOptionsText && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">⚠️ Please mark at least one option as correct</p>
                </div>
            )}

            {/* Options List */}
            <div className="space-y-3">
                {optionList.map((option, index) => (
                    <div
                        key={option.id}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                            option.isCorrect
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            {/* Correct Checkbox/Radio */}
                            <button
                                onClick={() => !disabled && toggleCorrect(option.id)}
                                disabled={disabled}
                                className={`mt-1 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                    option.isCorrect
                                        ? 'border-green-500 bg-green-500'
                                        : 'border-slate-300 dark:border-slate-600 hover:border-green-400'
                                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                title={option.isCorrect ? 'Correct answer' : 'Mark as correct'}
                            >
                                {option.isCorrect && <CheckIcon className="w-4 h-4 text-white" />}
                            </button>

                            {/* Option Content */}
                            <div className="flex-1 space-y-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2">
                                        {String.fromCharCode(65 + index)}.
                                    </span>
                                    <div className="flex-1">
                                        <TextInput
                                            value={option.text}
                                            onChange={(e: ComponentEvent<string>) => updateOption(option.id, { text: e.value })}
                                            placeholder={`Option ${String.fromCharCode(65 + index)}`}
                                            disabled={disabled || questionType === 'true_false'}
                                            className="w-full"
                                        />
                                    </div>
                                    {questionType !== 'true_false' && (
                                        <button
                                            onClick={() => removeOption(option.id)}
                                            disabled={disabled || optionList.length <= 2}
                                            className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Remove option"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Advanced Options Toggle */}
                                <button
                                    onClick={() => setExpandedOption(expandedOption === option.id ? null : option.id)}
                                    className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 flex items-center gap-1"
                                >
                                    <EditIcon className="w-3 h-3" />
                                    <span>{expandedOption === option.id ? 'Hide' : 'Show'} advanced options</span>
                                </button>

                                {/* Advanced Options Panel */}
                                {expandedOption === option.id && (
                                    <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                        {allowPartialCredit && questionType === 'mcq_multiple' && (
                                            <FieldLabel label="Partial Credit (%)" hint="Points awarded if this option is selected">
                                                <NumericInput
                                                    value={option.partialCredit || 0}
                                                    onChange={(e: ComponentEvent<number | undefined>) =>
                                                        updateOption(option.id, { partialCredit: e.value })
                                                    }
                                                    min={0}
                                                    max={100}
                                                    disabled={disabled}
                                                    placeholder="0-100"
                                                />
                                            </FieldLabel>
                                        )}
                                        <FieldLabel
                                            label="Description"
                                            hint="Explain why this option is correct or incorrect (shown to students after submission)"
                                        >
                                            <TextArea
                                                value={option.description || ''}
                                                onChange={(e: ComponentEvent<string>) => updateOption(option.id, { description: e.value })}
                                                placeholder="Explain why this answer is correct/incorrect..."
                                                disabled={disabled}
                                                rows={3}
                                            />
                                        </FieldLabel>
                                        <FieldLabel label="Feedback" hint="Optional feedback shown when this option is selected">
                                            <TextInput
                                                value={option.feedback || ''}
                                                onChange={(e: ComponentEvent<string>) => updateOption(option.id, { feedback: e.value })}
                                                placeholder="Additional feedback..."
                                                disabled={disabled}
                                            />
                                        </FieldLabel>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Info */}
            <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-4">
                <span>
                    {optionList.length} option{optionList.length !== 1 ? 's' : ''}
                </span>
                <span>•</span>
                <span className={correctCount === 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                    {correctCount} correct
                </span>
                {questionType === 'mcq_single' && correctCount > 1 && (
                    <>
                        <span>•</span>
                        <span className="text-yellow-600 dark:text-yellow-400">
                            ⚠️ Single choice questions should have only one correct answer
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export default OptionEditor;
